import { jsPDF, AcroFormTextField } from "jspdf";
import { T } from "@/lib/i18n";

export const DISCLAIMER = "This information is for education. It does not replace care from a qualified healthcare professional.";
const SITE = "brightstoriesnetwork.com";
const NAVY = [11, 23, 48];
const GOLD = [201, 162, 39];
const INK = [33, 47, 77];
const RED = [153, 27, 27];
const MUTED = [90, 100, 120];

const clean = (t) => t.replace(/\*\*/g, "").replace(/[\u201c\u201d]/g, '"').replace(/[\u2018\u2019]/g, "'").replace(/\u2014/g, " - ");

const loadSeal = () => new Promise((res) => {
  const img = new Image();
  img.onload = () => res(img);
  img.onerror = () => res(null);
  img.src = "/brand/seal.jpg";
});

const createWriter = (doc, seal, lang) => {
  const L = T[lang].pdf;
  const D = T[lang].disclaimer;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 54;
  const maxW = W - M * 2;
  const bottom = H - 80;
  let y = 0;
  let label = L.label;

  const header = (title) => {
    const first = Boolean(title);
    const bandH = first ? 150 : 46;
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, bandH, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, bandH, W, 3, "F");
    if (seal) doc.addImage(seal, "JPEG", M, first ? 22 : 8, first ? 52 : 30, first ? 52 : 30);
    doc.setTextColor(...GOLD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(first ? 11 : 9);
    doc.text(`BRIGHT STORIES NETWORK  ·  ${label}`, M + (first ? 66 : 40), first ? 44 : 27);
    if (!first) { y = 80; return; }
    doc.setTextColor(255, 255, 255);
    doc.setFont("times", "bold");
    doc.setFontSize(24);
    const tl = doc.splitTextToSize(clean(title), maxW);
    doc.text(tl, M, 100);
    y = Math.max(100 + tl.length * 26, 150) + 30;
  };

  const ensure = (h) => { if (y + h > bottom) { doc.addPage(); header(null); } };

  const para = (t, { size = 11, font = "helvetica", style = "normal", color = INK, indent = 0, gap = 6 } = {}) => {
    doc.setFont(font, style);
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(clean(t), maxW - indent);
    lines.forEach((ln) => {
      ensure(size + 4);
      doc.setFont(font, style);
      doc.setFontSize(size);
      doc.setTextColor(...color);
      doc.text(ln, M + indent, y);
      y += size + 4;
    });
    y += gap;
  };
  const bold = (t) => ({ style: t.startsWith("**") ? "bold" : "normal" });
  const crisis = () => para(L.crisis, { style: "bold", color: RED });

  const guide = (article, tag) => {
    label = tag || L.label;
    header(article.title);
    para(article.summary, { size: 12, style: "italic", gap: 12 });
    article.sections.forEach((s) => {
      if (s.alert) {
        ensure(70);
        doc.setDrawColor(...RED);
        doc.setLineWidth(1.5);
        doc.line(M - 10, y - 10, M - 10, y + 40);
      }
      if (s.h) { ensure(40); y += 6; para(s.h.replace(/^\d+\.\s*/, ""), { size: 15, font: "times", style: "bold", color: s.alert ? RED : NAVY, gap: 4 }); }
      if (s.quote) para(s.quote, { size: 12, font: "times", style: "italic", indent: 12 });
      s.p?.forEach((t) => para(t, bold(t)));
      s.list?.forEach((t) => para(`•  ${t}`, { indent: 10, gap: 2 }));
      s.levels?.forEach((l) => { para(l.t, { style: "bold", gap: 0 }); para(l.d, { indent: 10 }); });
      s.after?.forEach((t) => para(t, bold(t)));
      if (s.alert) crisis();
      y += 4;
    });
    if (article.note && !article.note.startsWith(D.slice(0, 20))) { y += 4; para(article.note, { size: 9, style: "italic", color: MUTED }); }
  };

  const safetyPlan = () => {
    label = L.plan;
    header(L.planTitle);
    para(L.planIntro, { size: 12, style: "italic", gap: 10 });
    doc.setFillColor(253, 242, 242);
    doc.setDrawColor(...RED);
    doc.setLineWidth(1);
    doc.roundedRect(M, y - 4, maxW, 84, 8, 8, "FD");
    y += 12;
    para(L.planCrisisH, { size: 13, font: "times", style: "bold", color: RED, indent: 14, gap: 2 });
    para(L.planCrisis1, { size: 10, indent: 14, gap: 0 });
    para(L.planCrisis2, { size: 10, indent: 14, gap: 38 });
    L.plan.forEach(([sh, hint, rows], i) => {
      const boxH = rows * 22 + 8;
      ensure(boxH + 50);
      para(sh, { size: 14, font: "times", style: "bold", color: NAVY, gap: 0 });
      para(hint, { size: 9, style: "italic", color: MUTED, gap: 2 });
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.8);
      doc.roundedRect(M, y, maxW, boxH, 6, 6, "S");
      doc.setDrawColor(220, 205, 160);
      doc.setLineWidth(0.4);
      for (let r = 1; r <= rows; r++) doc.line(M + 10, y + r * 22, M + maxW - 10, y + r * 22);
      const f = new AcroFormTextField();
      f.fieldName = `safety_plan_${i + 1}`;
      f.Rect = [M + 6, y + 4, maxW - 12, boxH - 8];
      f.multiline = true;
      f.fontSize = 11;
      f.textColor = "#212F4D";
      doc.addField(f);
      y += boxH + 18;
    });
    para(L.planClose, { size: 12, font: "times", style: "italic", color: NAVY });
  };

  return { guide, safetyPlan, W, H, M };
};

const footers = (doc, link, lang, skipFirst = false) => {
  const L = T[lang].pdf;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    if (!(skipFirst && i === 1)) {
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.8);
      doc.line(54, H - 66, W - 54, H - 66);
    }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...(skipFirst && i === 1 ? [235, 225, 200] : INK));
    doc.text(T[lang].disclaimer, W / 2, skipFirst && i === 1 ? H - 60 : H - 50, { align: "center" });
    if (skipFirst && i === 1) continue;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`${L.footerHelp}  ·  ${L.readOnline}: ${link}`, W / 2, H - 36, { align: "center" });
    doc.text(`${SITE}  ·  ${L.page} ${i} ${L.of} ${pages}`, W / 2, H - 24, { align: "center" });
  }
};

const start = async (lang) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  return { doc, w: createWriter(doc, await loadSeal(), lang) };
};

export const makeGuidePdf = async (article, link, lang = "en") => {
  const { doc, w } = await start(lang);
  w.guide(article);
  footers(doc, link || SITE, lang);
  doc.save(`BSN-${article.id}${lang === "es" ? "-es" : ""}.pdf`);
};

export const makeSafetyPlanPdf = async (lang = "en") => {
  const { doc, w } = await start(lang);
  w.safetyPlan();
  footers(doc, `${window.location.origin}/resources`, lang);
  doc.save(`${T[lang].files.plan}.pdf`);
};

export const makeSchoolPacket = async (articles, teen, lang = "en") => {
  const L = T[lang].pdf;
  const { doc, w } = await start(lang);
  const seal = await loadSeal();
  const toc = [];
  [...articles, teen].forEach((a, i) => {
    doc.addPage();
    toc.push([a.title, doc.getNumberOfPages()]);
    w.guide(a, i === articles.length ? L.teens : L.label);
  });
  doc.addPage();
  toc.push([L.planToc, doc.getNumberOfPages()]);
  w.safetyPlan();

  doc.setPage(1);
  const { W, H, M } = w;
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, H, "F");
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.5);
  doc.rect(24, 24, W - 48, H - 48, "S");
  if (seal) doc.addImage(seal, "JPEG", W / 2 - 60, 80, 120, 120);
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("BRIGHT STORIES NETWORK", W / 2, 236, { align: "center" });
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.setFontSize(36);
  doc.text(L.coverTitle, W / 2, 290, { align: "center" });
  doc.setFont("times", "italic");
  doc.setFontSize(16);
  doc.text(L.coverSub, W / 2, 318, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(210, 215, 230);
  doc.text(L.coverNote, W / 2, 342, { align: "center" });
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(L.contents, M + 30, 400);
  toc.forEach(([t, p], i) => {
    const ty = 428 + i * 26;
    doc.setFont("times", "normal");
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.textWithLink(`${i + 1}.  ${t}`, M + 30, ty, { pageNumber: p });
    doc.setTextColor(...GOLD);
    doc.text(String(p), W - M - 30, ty, { align: "right" });
  });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text(L.coverCrisis, W / 2, H - 100, { align: "center" });
  footers(doc, `${window.location.origin}/resources`, lang, true);
  doc.save(`${T[lang].files.packet}.pdf`);
};
